import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AdjustmentStateBadge,
  LineStatusBadge,
} from '@/components/adjustments/AdjustmentBadges'
import { ProductSearchPicker } from '@/components/po/ProductSearchPicker'
import {
  useAdjustment,
  useApplyAdjustment,
  useDeleteAdjustmentLine,
  useUpsertAdjustmentLine,
  type AdjustmentLine,
} from '@/hooks/useAdjustments'
import { SyncOffBanner } from '@/components/shared/SyncOffBanner'
import { ApiError } from '@/lib/api/rawFetch'
import { fmtDateTime } from '@/lib/dates'
import { cn } from '@/lib/utils'

const TH =
  'tbl-th border-b border-[var(--line-warm)] text-left'
const TD = 'border-b border-[var(--line-warm)] px-2 py-1.5 text-xs'

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n)
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

// Editable "New Available Quantity" for a draft line. The target quantity is
// what a draft carries — old_qty and the delta are only known once apply reads
// live Shopify stock, which is why the two columns beside this one stay blank.
function NewQtyCell({
  line,
  onSet,
  pending,
}: {
  line: AdjustmentLine
  onSet: (line: AdjustmentLine, qty: number) => void
  pending: boolean
}) {
  // Mid-edit the local draft wins; otherwise the cell tracks the cache.
  const [draft, setDraft] = React.useState<string | null>(null)
  const value = draft ?? String(line.new_qty ?? 0)

  function commit() {
    if (draft == null) return
    const trimmed = draft.trim()
    setDraft(null)
    if (trimmed === '') return
    const qty = Number(trimmed)
    if (!Number.isInteger(qty) || qty < 0) return
    if (qty === (line.new_qty ?? 0)) return
    onSet(line, qty)
  }

  return (
    <div className="flex items-center justify-end">
      <input
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={value}
        disabled={pending}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            setDraft(null)
          }
        }}
        className="w-[64px] border border-[var(--line-input)] bg-transparent px-1 py-0.5 text-right text-xs font-semibold tabular-nums text-[var(--text-primary)]"
        aria-label={`New quantity for ${line.product_title || line.sku}`}
        data-testid={`adjustment-qty-input-${line.adj_line_id}`}
      />
    </div>
  )
}

function MetaItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
        {label}
      </span>
      <span className="text-xs text-[var(--ink-2)]">{value}</span>
    </div>
  )
}

/** Stocky's Stock Adjustment detail. Two shapes behind one screen: a performed
 *  record is the read-only receipt of a push Shopify took, a draft is an
 *  editable list of target quantities that hasn't been pushed yet. */
export default function AdjustmentDetail() {
  const { adjustmentId } = useParams<{ adjustmentId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const id = adjustmentId ?? ''
  const { data: adjustment, isLoading } = useAdjustment(id || null)
  const upsert = useUpsertAdjustmentLine(id)
  const remove = useDeleteAdjustmentLine(id)
  const apply = useApplyAdjustment(id)

  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [pendingLineId, setPendingLineId] = React.useState<number | null>(null)
  // Whatever the last apply attempt said, which is fresher than the copy the
  // detail query carries until it refetches.
  const [applyError, setApplyError] = React.useState<string | null>(null)

  const handleSetQty = React.useCallback(
    (line: AdjustmentLine, qty: number) => {
      setPendingLineId(line.adj_line_id)
      upsert.mutate(
        { variant_id: line.variant_id, new_qty: qty },
        {
          onError: (err) =>
            toast.error(
              err instanceof Error ? err.message : 'Failed to save the new quantity'
            ),
          onSettled: () => setPendingLineId(null),
        }
      )
    },
    [upsert]
  )

  if (isLoading || !adjustment) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[var(--mute)]">
        Loading…
      </div>
    )
  }

  const isDraft = adjustment.status === 'draft'
  const lines = adjustment.lines
  const locationName = adjustment.location_name || 'this location'
  const banner = applyError ?? adjustment.apply_error
  const addedVariantIds = new Set(lines.map((l) => l.variant_id))

  function handleApply() {
    apply.mutate(undefined, {
      onSuccess: (result) => {
        setConfirmOpen(false)
        if (result.ok) {
          setApplyError(null)
          toast.success(
            `Adjustment applied to Shopify — ${result.adjusted} ${plural(
              result.adjusted,
              'variant',
              'variants'
            )} adjusted`
          )
          return
        }
        // HTTP 200 with ok=false: Shopify refused the batch. The record stays a
        // draft, so the same button is the retry.
        setApplyError(result.error ?? 'Shopify rejected the adjustment')
        toast.error('Apply failed — the adjustment is still a draft, so you can retry')
      },
      onError: (err) => {
        setConfirmOpen(false)
        if (err instanceof ApiError && err.status === 503) {
          const msg = 'Shopify is not configured / offline mode — cannot apply'
          setApplyError(msg)
          toast.error(msg)
          return
        }
        if (err instanceof ApiError && err.status === 409) {
          toast.error('This adjustment is no longer a draft — reloading it')
          qc.invalidateQueries({ queryKey: ['adjustment', id] })
          return
        }
        const msg = err instanceof Error ? err.message : 'Could not apply the adjustment'
        setApplyError(msg)
        toast.error(msg)
      },
    })
  }

  function handleAddVariant(variantId: number) {
    if (!variantId || addedVariantIds.has(variantId)) return
    // Lands as a 0 target so the row exists immediately; the operator then types
    // the real count into the New Available Quantity cell.
    upsert.mutate(
      { variant_id: variantId, new_qty: 0 },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to add the variant'),
      }
    )
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="brand-theme flex min-h-screen flex-col bg-[var(--surface-page)] text-[var(--text-primary)]">
        {/* No TopTabs on this page, so the sync-off banner goes here. */}
        <SyncOffBanner />
        {/* Page header */}
        <div className="flex items-center gap-3 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[14px]">
          <Button
            variant="ghost"
            size="sm"
            className="h-[22px] px-2 text-[11px] text-[var(--ink-2)] hover:text-[var(--text-primary)]"
            onClick={() => {
              qc.invalidateQueries({ queryKey: ['adjustments'] })
              navigate('/adjustments')
            }}
            data-testid="adjustment-back-btn"
          >
            ← Back
          </Button>
          <h1 className="flex items-center gap-2 text-[18px] font-semibold leading-tight tracking-[-0.01em] text-[var(--text-primary)]">
            Stock Adjustment #{adjustment.adjustment_id}
            <AdjustmentStateBadge status={adjustment.status} />
          </h1>
          <div className="flex-1" />
          {isDraft && (
            <Button
              size="sm"
              className="h-[22px] px-2 text-[11px] font-semibold"
              onClick={() => setConfirmOpen(true)}
              disabled={apply.isPending || lines.length === 0}
              data-testid="adjustment-apply-btn"
            >
              {apply.isPending
                ? 'Applying…'
                : banner
                  ? 'Retry apply to Shopify…'
                  : 'Apply to Shopify…'}
            </Button>
          )}
        </div>

        {/* Meta */}
        <div
          className="flex flex-wrap items-start gap-x-8 gap-y-3 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[12px]"
          data-testid="adjustment-meta"
        >
          <MetaItem label="Location" value={adjustment.location_name || '—'} />
          <MetaItem label="Reason" value={adjustment.reason || '—'} />
          <MetaItem label="Notes" value={adjustment.notes || '—'} />
          <MetaItem
            label="Created"
            value={
              [adjustment.created_by || '—', fmtDateTime(adjustment.created_at)]
                .filter(Boolean)
                .join(' · ')
            }
          />
          <MetaItem
            label="Adjusted"
            value={
              adjustment.adjusted_at
                ? [adjustment.adjusted_by || '—', fmtDateTime(adjustment.adjusted_at)]
                    .filter(Boolean)
                    .join(' · ')
                : '—'
            }
          />
          {adjustment.session_id != null && (
            <MetaItem
              label="Source"
              value={
                <Link
                  to={`/stocktakes/${adjustment.session_id}`}
                  className="font-semibold text-[var(--brand)] underline"
                  data-testid="adjustment-stocktake-link"
                >
                  From Stocktake #{adjustment.session_id}
                </Link>
              }
            />
          )}
        </div>

        {banner && (
          <p
            className="border-b border-[var(--line-warm)] bg-[var(--status-rejected-bg)] px-[18px] py-1.5 text-xs text-[var(--status-rejected-fg)]"
            data-testid="adjustment-apply-error"
          >
            Last apply failed: {banner}. Nothing was left half-applied — deltas are
            recomputed against live stock on every attempt, so retrying is safe.
          </p>
        )}

        {/* Draft-only: pick the variants this correction covers */}
        {isDraft && (
          <div
            className="border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[12px]"
            data-testid="adjustment-add-variant"
          >
            <div className="mb-1 text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
              Add variant
            </div>
            <div className="max-w-[560px]">
              <ProductSearchPicker
                onPick={(item) => handleAddVariant(item.variant_id)}
                addedVariantIds={addedVariantIds}
                disabled={apply.isPending}
                autoPickOnBarcodeMatch
              />
            </div>
          </div>
        )}

        {lines.length === 0 ? (
          <div
            className="px-[18px] py-6 text-xs text-[var(--mute)]"
            data-testid="adjustment-no-lines"
          >
            {isDraft
              ? 'No variants on this draft yet — search above to add the first one.'
              : 'This adjustment moved nothing: the count found no discrepancies.'}
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 170 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 180 }} />
                {isDraft && <col style={{ width: 50 }} />}
              </colgroup>
              <thead>
                <tr>
                  <th className={TH}>Product</th>
                  <th className={TH}>Variant</th>
                  <th className={TH}>SKU</th>
                  <th className={TH}>Status</th>
                  <th className={`${TH} text-right whitespace-normal`}>Old Available Quantity</th>
                  <th className={`${TH} text-right`}>Adjustment</th>
                  <th className={`${TH} text-right whitespace-normal`}>New Available Quantity</th>
                  {isDraft && <th className={TH} aria-label="Remove" />}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, index) => {
                  const d = l.delta
                  return (
                    <tr
                      key={l.adj_line_id}
                      className={
                        index % 2 === 1
                          ? 'bg-[var(--surface-zebra)]'
                          : 'bg-[var(--surface-card)]'
                      }
                      data-testid={`adjustment-line-${l.adj_line_id}`}
                    >
                      <td className={`${TD} font-medium text-[var(--text-primary)]`}>
                        {l.product_title || '—'}
                      </td>
                      <td className={`${TD} text-[var(--ink-2)]`}>
                        {l.variant_title || '—'}
                      </td>
                      <td className={`${TD} font-mono text-[var(--ink-2)]`}>
                        {l.sku || '—'}
                      </td>
                      <td className={TD}>
                        {l.apply_status === 'failed' ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">
                                <LineStatusBadge status={l.apply_status} />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs text-left">
                              {l.apply_error || 'Shopify rejected this line.'}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <LineStatusBadge status={l.apply_status} />
                        )}
                      </td>
                      {/* Blank on a draft: the old quantity is read live at apply
                          time, so quoting one now would be a guess. */}
                      <td className={`${TD} text-right tabular-nums text-[var(--ink-2)]`}>
                        {l.old_qty ?? '—'}
                      </td>
                      <td className={`${TD} text-right tabular-nums`}>
                        {d == null ? (
                          <span className="text-[var(--mute)]">—</span>
                        ) : (
                          <span
                            className={cn(
                              'font-semibold',
                              // Negative = stock we thought was there and isn't
                              // (shrinkage, miscount, theft), the key signal, so
                              // it stays red, softened to the status red.
                              d > 0 && 'text-[var(--status-approved-fg)]',
                              d < 0 && 'text-[var(--status-rejected-fg)]',
                              d === 0 && 'font-normal text-[var(--mute)]'
                            )}
                            data-testid={`adjustment-delta-${l.adj_line_id}`}
                          >
                            {signed(d)}
                          </span>
                        )}
                      </td>
                      <td
                        className={`${TD} text-right font-semibold tabular-nums text-[var(--text-primary)]`}
                      >
                        {isDraft ? (
                          <NewQtyCell
                            line={l}
                            onSet={handleSetQty}
                            pending={pendingLineId === l.adj_line_id}
                          />
                        ) : (
                          (l.new_qty ?? '—')
                        )}
                      </td>
                      {isDraft && (
                        <td className={`${TD} text-right`}>
                          <button
                            type="button"
                            onClick={() =>
                              remove.mutate(l.adj_line_id, {
                                onError: (err) =>
                                  toast.error(
                                    err instanceof Error
                                      ? err.message
                                      : 'Failed to remove the line'
                                  ),
                              })
                            }
                            disabled={remove.isPending}
                            className="text-[var(--mute)] hover:text-destructive"
                            aria-label={`Remove ${l.product_title || l.sku}`}
                            data-testid={`adjustment-line-delete-${l.adj_line_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Apply confirm — this is a write to Shopify stock. The confirm button
            says exactly what it does, so the weight is in the wording. */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent
            className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
            data-testid="adjustment-apply-dialog"
          >
            <DialogHeader>
              <DialogTitle>Apply this adjustment?</DialogTitle>
              <DialogDescription asChild>
                <p className="text-xs">
                  {lines.length} {plural(lines.length, 'variant', 'variants')} will be set
                  to their new quantities at {locationName}.{' '}
                  <span
                    className="font-semibold text-[var(--status-awaiting-fg)]"
                    data-testid="adjustment-apply-caution"
                  >
                    This cannot be undone.
                  </span>
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmOpen(false)}
                disabled={apply.isPending}
                data-testid="adjustment-dialog-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={apply.isPending || lines.length === 0}
                data-testid="adjustment-apply-confirm-btn"
              >
                {apply.isPending
                  ? 'Applying…'
                  : `Apply ${lines.length} ${plural(
                      lines.length,
                      'variant',
                      'variants'
                    )} to Shopify`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}
